import type { IncludedServices, SearchResult } from '@/scopes/search/search/infrastructure/api/types';

import type { IconGlyph } from '@sunwebgroup/ui-components';
import { ProductCard } from './ProductCard';
import { useSearchStore } from '@/scopes/search/search/infrastructure/store/useSearchStore';
import { createSearchProductClickEvent } from '@/scopes/search/search/domain/analytics';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';

export interface AccommodationService {
  icon?: IconGlyph;
  text: string;
  key: string;
}

function transformIncludedServices(includedServices?: IncludedServices): AccommodationService[] {
  const excludedServices = ['accommodation', 'flight', 'bus'];

  if (!includedServices) {
    return [];
  }

  // Filter out excluded services and create a new array of accommodation services
  const services: AccommodationService[] = Object.entries(includedServices)
    .filter(([key]) => !excludedServices.includes(key))
    .map(([key, value]) => ({
      key,
      icon: getIconForService(key),
      text: value,
    }));

  return services;
}

const getIconForService = (service: string): IconGlyph | undefined => {
  const serviceIconMap: Record<string, IconGlyph> = {
    carrental: 'IconCarFront',
    skimaterial: 'IconSkiMaterial',
    skipass: 'IconSkiPass',
    transfer: 'IconBusFront',
  };

  return serviceIconMap[service];
};

export function Result(result: SearchResult) {
  const { filterSelection } = useSearchStore();
  const { sendEvent } = useAnalytics();

  const onClickHandler = () => {
    sendEvent(createSearchProductClickEvent(result));
  };

  return (
    <ProductCard
      accommodationId={result.id}
      accommodationServices={transformIncludedServices(result.price?.includedServices)}
      arrivalDate={result.arrivalDateDetail}
      departureDate={result.departureDateDetail}
      departureDateRaw={result.price?.acmInformation?.departureDateRaw}
      durationRaw={result.price?.acmInformation?.durationRaw}
      campaign={result.campaign}
      href={result.urlWithFilters}
      id={result.id}
      imageSources={result.images}
      isUnavailable={result.isUnavailable}
      yieldCampaign={result.price?.yieldCampaignName}
      yieldCampaignLogoSource={result.price?.yieldCampaignLogo?.src}
      locations={result.locations}
      mealPlan={result.featuredFilters?.mealplan}
      name={result.name ?? ''}
      partyComposition={result.featuredFilters?.occupancy}
      price={result.price?.averagePrice}
      priceDiscounts={result.price?.priceDiscounts}
      rating={result.rating ?? 0}
      transportType={result.price?.acmInformation?.transportType}
      userRating={result.userRating}
      userRatingText={result.userRatingText}
      usps={result.usps}
      outboundDepartureAirportName={result.outboundDepartureAirportName}
      collection={result.collection}
      isMarketExclusive={result.isMarketExclusive}
      isSustainable={result.isSustainable}
      participants={filterSelection?.Participants}
      onClick={onClickHandler}
    />
  );
}
